/// <amd-module name="Wit/QueriesHub/QueryList/Components/DeletedQueries" />
import * as React from "react";
import * as Utils from "VSS/Core/Util/String";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITableColumn, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { VssPersona } from "VSSUI/VssPersona";
import { QueryItem } from "Wit/Common/QueryUtils/Models";
import * as RoutingUtils from "Wit/Common/QueryUtils/RoutingUtils";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import { IDeletedQueryService } from "Wit/QueriesHub/QueryList/Services/IDeletedQueryService";
import * as Resources from "Wit/QueriesHub/Resources";

const c_msToDays = 24 * 60 * 60 * 1000;

export class DeletedQueries extends VssComponent {
    private _ttiMarked: boolean;
    private _dataService: IDeletedQueryService;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this._dataService = context.pageContext.getService<IDeletedQueryService>("IDeletedQueryService");
        this._ttiMarked = false;
    }

    public componentDidMount() {
        this._markTTI();
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="deleted-queries-page flex-grow flex-column">
                    <Header
                        title={Resources.DeletedQueriesHeader}
                        titleSize={TitleSize.Large}
                        backButtonProps={{ onClick: this._onClickHeaderBack }}
                    />
                    <div className="deleted-queries-page-content">
                        <MessageCard severity={MessageCardSeverity.Info} className="deleted-queries-card">
                            <span>{format(Resources.DeletedQueriesMessageCardText, this._dataService.daysToKeep)}</span>
                        </MessageCard>
                        <Observer queries={this._dataService.data}>
                            {(observerProps: { queries: QueryItem[] }) => {
                                if (observerProps.queries.length) {
                                    return (
                                        <Card className="deleted-queries-card bolt-table-card">
                                            <Table columns={this._tableColumns} itemProvider={this._dataService.data} singleClickActivation={false} />
                                        </Card>
                                    );
                                } else {
                                    return null;
                                }
                            }}
                        </Observer>
                    </div>
                </Page>
            </Surface>
        );
    }

    private _onClickHeaderBack = () => {
        const queryFolderUrl = RoutingUtils.getQueryFolderUrl(this.context.pageContext);
        onClickFPS(this.context.pageContext, queryFolderUrl, true);
    };

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<QueryItem>, tableItem: QueryItem): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontWeightSemiBold fontSizeM scroll-hidden"
            >
                <Icon iconName="ViewList" className="icon-margin font-size-ml font-weight-normal" />
                <span className="text-ellipsis">{tableItem.name}</span>
            </SimpleTableCell>
        );
    };

    private _renderDeletedByCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<QueryItem>,
        tableItem: QueryItem
    ): JSX.Element => {
        const identity = tableItem.lastModifiedBy;
        const imageUrl = getIdentityImageUrl(this.context.pageContext, identity);
        const dayInSeconds = 60 * 60 * 24;
        const text = Utils.format(Resources.Deleted, identity.displayName, QueryUtils.getFriendlyDate(tableItem.lastModifiedDate, dayInSeconds));
        return (
            <SimpleTableCell ariaRowIndex={rowIndex} columnIndex={columnIndex} key={`col-${columnIndex} row-${rowIndex}`}>
                <VssPersona cssClass={"margin-right-8"} displayName={identity.displayName} imageUrl={imageUrl} size="extra-small"></VssPersona>
                <Tooltip overflowOnly={false} text={text}>
                    <span className="text-ellipsis">{text}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private _renderDestructionCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<QueryItem>,
        tableItem: QueryItem
    ): JSX.Element => {
        const daysDeleted = Math.round((new Date().valueOf() - tableItem.lastModifiedDate.valueOf()) / c_msToDays);
        // Return 0 if the item has been deleted for longer than daysToKeep
        // this could happen because we clean up deleted queries 1x per week
        const daysRemaining = Math.max(this._dataService.daysToKeep - daysDeleted, 0);
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden"
            >
                <span className="text-ellipsis">
                    {daysRemaining === 1 ? Resources.DaysRemainingSingular : format(Resources.DaysRemaining, daysRemaining)}
                </span>
            </SimpleTableCell>
        );
    };

    private _renderActionCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<QueryItem>, tableItem: QueryItem): JSX.Element => {
        const restoreQuery = () => {
            this._dataService.restore(tableItem);
        };

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden bolt-table-cell-side-action"
            >
                <div className="deleted-queries-right-align flex-row">
                    <Button
                        primary={false}
                        className="flex-end bolt-table-cell-content-reveal"
                        subtle={true}
                        iconProps={{ iconName: "RevToggleKey" }}
                        ariaLabel={Resources.Restore}
                        tooltipProps={{ text: Resources.Restore }}
                        onClick={restoreQuery}
                        excludeTabStop={true}
                    />
                </div>
            </SimpleTableCell>
        );
    };

    private _markTTI() {
        if (!this._ttiMarked) {
            this._ttiMarked = true;
            const service = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            service.markTimeToInteractive("Wit", "Queries.DeletedQueries");
        }
    }

    private _tableColumns: ITableColumn<QueryItem>[] = [
        {
            id: "name",
            width: -20,
            name: Resources.NameLabel,
            className: "deleted-queries-cell",
            renderCell: this._renderNameCell,
            columnStyle: TableColumnStyle.Primary
        },
        {
            id: "deleted-by",
            width: -40,
            name: Resources.DeletedByColumn,
            className: "deleted-queries-cell",
            renderCell: this._renderDeletedByCell,
            columnStyle: TableColumnStyle.Secondary
        },
        {
            id: "destruction",
            width: -20,
            name: Resources.PermanentDeletionColumnName,
            className: "deleted-queries-cell",
            renderCell: this._renderDestructionCell,
            columnStyle: TableColumnStyle.Secondary
        },
        {
            id: "action",
            width: -20,
            className: "deleted-queries-cell",
            renderCell: this._renderActionCell,
            columnStyle: TableColumnStyle.Secondary
        }
    ];
}

Components.add("deleted-queries-component", DeletedQueries);
